<script setup>
  import {computed, inject, onBeforeMount} from "vue";

  import ComponentBreadcrumb from "@components/Breadcrumb/Breadcrumb.vue";
  import ComponentCard from "@components/Cards/Card.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentTextarea from "@components/Textarea/Textarea.vue";
  import {useForm, usePage} from "@inertiajs/vue3";
  import {MedButton, MedCheckbox, MedFormField, MedLabel} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PagesSettingsShow",
  });

  const page = usePage();

  const privacy = inject("$privacy");

  const privacyForm = useForm({
    privacy_responsible_person: page.props.client?.privacy_responsible_person,
    privacy_address: page.props.client?.privacy_address,
  });

  /**
   * default values when configuration was not saved yet
   */
  const accountConfigurationForm = useForm({
    activateAiFeatures: page.props.can?.activate_ai_features ?? false,
    notifyComments: true,
    doNotCloseClaimsAutomatically: false,
    communicationPatientWarning: true,
    aiBasedPatientRephraseWarning: true,
    aiBasedSummaryWarning: true,
    aiBasedPrioritizationWarning: true,
  });

  const mail = computed(() => privacy.getCurrentUser()?.email);

  onBeforeMount(() => {
    mapUserSettings();
  });

  const mapUserSettings = () => {
    if (page.props.settings.data.length > 0) {
      for (const objectItem of page.props.settings.data) {
        accountConfigurationForm[objectItem.name] = objectItem.amount;
      }
    }
  };

  const saveAccountConfiguration = () => {
    accountConfigurationForm
      .transform((data) => {
        // whenever this form is saved, we store a second flag which indicates,
        // if the user has made an active decision regarding ai feature activation
        if ((page.props.can?.activate_ai_features ?? false) !== data.activateAiFeatures) {
          data.hideAiOptInDialog = true;
        }
        return data;
      })
      .post(route("settings.store", {user: privacy.getCurrentUser().id}), {
        preserveScroll: true,
      });
  };

  const savePrivacySettings = () => {
    privacyForm.patch(route("privacy-settings.update", {client: page.props.client.id}), {
      preserveScroll: true,
    });
  };
</script>

<template>
  <ComponentBreadcrumb
    class="mt-2"
    :links="[
      {
        label: 'Einstellungen',
      },
    ]"
  />

  <section>
    <h2 class="sr-only text-2xl font-semibold">Einstellungen</h2>
    <article class="flex flex-col items-start space-y-6 py-4 lg:flex-row lg:space-y-0 lg:space-x-6">
      <!-- article::main -->
      <div class="w-full space-y-6 lg:max-w-4xl">
        <ComponentCard
          title="Verantwortlicher im Sinne der DSGVO"
          subtitle="gemäß Art. 4 Nr. 7 DSGVO"
          class="rounded-lg border-2 border-gray-200"
          :test-id="getTestId('card-privacy-settings')"
        >
          <ul class="min-w-full space-y-4">
            <li>
              <h3 class="text-xl font-semibold">Kontaktdaten des Datenschutzbeauftragten der Apotheke</h3>
              <p class="text-sm text-gray-500">
                Diese Angaben werden für die Erstellung von PDF-Dokumenten benötigt (bspw. Patientendokumente im
                Zusammenhang mit der digitalen Signatur oder Datenschutzinformationen im Zusammenhang mit Fragebögen).
              </p>

              <ul class="mt-4 space-y-4">
                <li>
                  <ComponentInput
                    v-model="privacyForm.privacy_responsible_person"
                    label="Verantwortliche Person *"
                    :validation="privacyForm.errors.privacy_responsible_person"
                    maxlength="40"
                    class="max-w-md"
                  />
                </li>

                <li>
                  <ComponentTextarea
                    v-model="privacyForm.privacy_address"
                    :validation="privacyForm.errors.privacy_address"
                    label="Anschrift / Kontaktdaten *"
                    placeholder="Adresse, Telefon oder E-Mail mit Zeilenumbrüchen"
                    class="max-w-md"
                    rows="6"
                    helper-text="Darf maximal fünf Zeilenumbrüche beinhalten."
                  />
                </li>
              </ul>
            </li>
          </ul>

          <div class="text-right text-sm text-gray-500">* Pflichtangaben</div>

          <template #actions>
            <MedButton
              :data-test="getTestId('card-save-privacy-settings-button')"
              variant="primary"
              @click="savePrivacySettings"
            >
              Speichern
            </MedButton>
          </template>
        </ComponentCard>

        <component-card
          title="Sonstige Einstellungen"
          class="rounded-lg border-2 border-gray-200"
          :test-id="getTestId('card-account-settings')"
        >
          <ul class="min-w-full space-y-4">
            <li v-if="$page.props.client.license.is_pro">
              <h3 class="text-xl font-semibold">Künstliche Intelligenz</h3>

              <ul>
                <li class="-mx-1 border-b border-gray-300 px-1 py-3">
                  <MedFormField orientation="horizontal">
                    <MedCheckbox
                      v-model="accountConfigurationForm.activateAiFeatures"
                      variant="accent"
                      :data-test="getTestId('card-activate-ai-features-checkbox')"
                    />

                    <MedLabel>KI-Funktionen im MediCheck aktivieren</MedLabel>
                  </MedFormField>
                </li>
              </ul>
            </li>

            <li v-if="$page.props.client.license.is_pro">
              <h3 class="text-xl font-semibold">Benachrichtigungseinstellungen</h3>

              <ul>
                <li class="-mx-1 border-b border-gray-300 px-1 py-3">
                  <MedFormField orientation="horizontal">
                    <MedCheckbox
                      v-model="accountConfigurationForm.notifyComments"
                      variant="accent"
                      :data-test="getTestId('card-notify-comments-checkbox')"
                    />

                    <MedLabel>
                      Benachrichtigung bei Kommentaren per E-Mail
                      <span v-if="mail">an {{ mail }}</span>
                    </MedLabel>
                  </MedFormField>
                </li>
              </ul>
            </li>

            <li>
              <h3 class="text-xl font-semibold">Check-Treffer ein/ausklappen</h3>

              <ul>
                <li class="-mx-1 border-b border-gray-300 px-1 py-3">
                  <MedFormField orientation="horizontal">
                    <MedCheckbox
                      v-model="accountConfigurationForm.doNotCloseClaimsAutomatically"
                      variant="accent"
                      :data-test="getTestId('card-do-not-close-claims-automatically-checkbox')"
                    />

                    <MedLabel>Check-Treffer nicht automatisch schließen</MedLabel>
                  </MedFormField>
                </li>
              </ul>
            </li>

            <li>
              <h3 class="text-xl font-semibold">Anzeigeeinstellungen Hinweismeldungen</h3>

              <ul>
                <li class="-mx-1 border-b border-gray-300 px-1 py-3">
                  <MedFormField orientation="horizontal">
                    <MedCheckbox
                      v-model="accountConfigurationForm.communicationPatientWarning"
                      variant="accent"
                      :data-test="getTestId('card-communication-patient-warning-checkbox')"
                    />

                    <MedLabel>Meldung „Hinweis Ergebnisbericht Patient“ anzeigen</MedLabel>
                  </MedFormField>
                </li>

                <li v-if="$page.props.client.license.is_pro" class="-mx-1 border-b border-gray-300 px-1 py-3">
                  <MedFormField orientation="horizontal">
                    <MedCheckbox
                      v-model="accountConfigurationForm.aiBasedPatientRephraseWarning"
                      variant="accent"
                      :data-test="getTestId('card-ai-based-patient-rephrase-warning-checkbox')"
                    />

                    <MedLabel>Meldung „KI-gestützte Vereinfachung der Sprache starten?“ anzeigen</MedLabel>
                  </MedFormField>
                </li>

                <li v-if="$page.props.client.license.is_pro" class="-mx-1 border-b border-gray-300 px-1 py-3">
                  <MedFormField orientation="horizontal">
                    <MedCheckbox
                      v-model="accountConfigurationForm.aiBasedSummaryWarning"
                      variant="accent"
                      :data-test="getTestId('card-ai-based-patient-rephrase-warning-checkbox')"
                    />

                    <MedLabel>Meldung „KI-unterstützte Erstellung einer Zusammenfassung starten?“ anzeigen</MedLabel>
                  </MedFormField>
                </li>

                <li v-if="$page.props.can.allow_ai_prioritization" class="-mx-1 border-b border-gray-300 px-1 py-3">
                  <MedFormField orientation="horizontal">
                    <MedCheckbox
                      v-model="accountConfigurationForm.aiBasedPrioritizationWarning"
                      variant="accent"
                      :data-test="getTestId('card-ai-based-prioritization-warning-checkbox')"
                    />

                    <MedLabel>Meldung „KI-Priorisierung starten?“ anzeigen</MedLabel>
                  </MedFormField>
                </li>
              </ul>
            </li>
          </ul>

          <template #actions>
            <MedButton
              :data-test="getTestId('card-save-account-settings-button')"
              variant="primary"
              @click="saveAccountConfiguration"
            >
              Speichern
            </MedButton>
          </template>
        </component-card>
      </div>
    </article>
  </section>
</template>
